import { useEffect } from 'react'
import {
  addCrossClientLandingCompletedExecutionProgresses,
  addCrossClientLandingCompletedExecutions,
  resetCrossClientLanding,
  selectCrossClientLandingCompletedExecutionProgresses,
  selectCrossClientLandingCompletedExecutions,
  setCrossClientLandingCompletedExecutionsLoading,
  setCrossClientLandingRunningExecutions,
  setCrossClientLandingRunningExecutionsLoading,
  setCrossClientLandingTeammates,
  setCrossClientLandingTeammatesLoading,
  setCrossClientLandingTotalCompletedExecutions,
} from '../../../store/crossClient/crossClientLanding'
import { CrossClientQueryPhases } from '../../../models/CrossClient'
import {
  useLazyGetAllCrossClientQueryExecutionsQuery,
  useLazyGetCrossClientQueryExecutionProgressQuery,
  useLazyGetCrossClientQueryExecutionsQuery,
} from '../../../store/crossClient/crossClient'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetAllUsersQuery } from '../../../store/user/user'

const PAGE_SIZE = 5

export const CrossClientLandingInitialization = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const completedExecutions = useAppSelector(selectCrossClientLandingCompletedExecutions)
  const completedExecutionProgresses = useAppSelector(selectCrossClientLandingCompletedExecutionProgresses)

  const [triggerGetCrossClientExecutions] = useLazyGetCrossClientQueryExecutionsQuery()
  const [triggerGetAllCrossClientExecutions] = useLazyGetAllCrossClientQueryExecutionsQuery()
  const [triggerGetCrossClientExecutionProgress] = useLazyGetCrossClientQueryExecutionProgressQuery()
  const [triggerGetAllUsers] = useLazyGetAllUsersQuery()

  useEffect(() => {
    const getCompletedExecutions = async () => {
      try {
        dispatch(setCrossClientLandingCompletedExecutionsLoading(true))
        const executions = await triggerGetCrossClientExecutions({
          accountId,
          phase: CrossClientQueryPhases.COMPLETED,
          pageRequest: { page: 0, size: PAGE_SIZE },
        }).unwrap()
        if (!executions.data.length) {
          return
        }
        dispatch(addCrossClientLandingCompletedExecutions(executions.data))
        dispatch(setCrossClientLandingTotalCompletedExecutions(executions.page.totalElements))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCrossClientLandingCompletedExecutionsLoading(false))
      }
    }
    getCompletedExecutions()
  }, [dispatch, accountId, triggerGetCrossClientExecutions])

  useEffect(() => {
    const getCrossClientLandingTeammates = async () => {
      try {
        const teammates = await triggerGetAllUsers({ accountId, identityTypes: ['idp'] }).unwrap()
        if (teammates?.data) {
          dispatch(setCrossClientLandingTeammates(teammates.data))
        }
      } finally {
        dispatch(setCrossClientLandingTeammatesLoading(false))
      }
    }
    getCrossClientLandingTeammates()
  }, [dispatch, accountId, triggerGetAllUsers])

  useEffect(() => {
    if (!completedExecutions.length) {
      return
    }
    const missingProgressExecutionIds = completedExecutions
      .map((e) => e.id)
      .filter((executionId) => !completedExecutionProgresses.some((p) => p.executionId === executionId))
    if (!missingProgressExecutionIds.length) {
      return
    }
    const getMissingCompletedExecutionProgress = async () => {
      try {
        const progresses = []
        for (const executionId of missingProgressExecutionIds) {
          const progress = await triggerGetCrossClientExecutionProgress({ accountId, executionId }).unwrap()
          if (progress) {
            progresses.push(progress)
          }
        }
        if (progresses.length) {
          dispatch(addCrossClientLandingCompletedExecutionProgresses(progresses))
        }
      } catch (e) {
        console.error(e)
      }
    }
    getMissingCompletedExecutionProgress()
  }, [dispatch, accountId, completedExecutions, completedExecutionProgresses, triggerGetCrossClientExecutionProgress])

  useEffect(() => {
    const getRunningExecutions = async () => {
      try {
        dispatch(setCrossClientLandingCompletedExecutionsLoading(true))
        const executions = await triggerGetAllCrossClientExecutions({
          accountId,
          phase: CrossClientQueryPhases.PROCESSING,
        }).unwrap()
        if (!executions.data.length) {
          return
        }
        dispatch(setCrossClientLandingRunningExecutions(executions.data))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCrossClientLandingRunningExecutionsLoading(false))
      }
    }
    getRunningExecutions()
  }, [dispatch, accountId, triggerGetAllCrossClientExecutions])

  useEffect(() => {
    dispatch(resetCrossClientLanding())
  }, [dispatch])

  return null
}
